import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { useStaticQuery, graphql } from 'gatsby';
import { LayoutContext, BlogNav, NavSkipButton } from '@lucid/components';

import type { Lucid } from 'types/types';

const TechBlogNav: FC<PropsWithChildren> = () => {
  const data = useStaticQuery<Lucid.Query.Hooks.UseTechBlog>(graphql`
    query TechBlogNavQuery {
      directus {
        tech_blog_category {
          id
          translations {
            languages_code {
              short_code
            }
            name
            path {
              url
            }
          }
        }
        # Get all published blog posts, their categories, and translations
        # we will filter and group these to see if we should show a category (must have at least 5 posts)
        tech_blog_post(
          limit: 10000
          filter: { _and: [{ status: { _eq: "published" } }, { is_private: { _neq: true } }] }
        ) {
          categories {
            tech_blog_category_id {
              id
            }
          }
          translations(filter: { is_published: { _eq: true } }) {
            languages_code {
              short_code
            }
          }
        }
      }
    }
  `);

  const { shortCode } = useContext(LayoutContext);

  const {
    directus: { tech_blog_category, tech_blog_post },
  } = data;

  type CategoryCount = {
    category: Lucid.Directus.Generated.Tech_Blog_Category;
    postCount: number;
  };

  // get category translations
  // TODO see if we can offload this calculation to the server - cound it on change and set a value of "enabled" or something
  const categoryCount = (tech_blog_category || []).reduce((previous, category) => {
    const { id, translations } = category;
    const translation = translations?.find(translation => translation?.languages_code?.short_code === shortCode);
    const postCount = (tech_blog_post || []).filter(post => {
      const postCategories = post.categories?.map(cat => cat.tech_blog_category_id?.id);
      const hasTranslation = post.translations?.some(tr => tr.languages_code?.short_code === shortCode);

      return postCategories?.includes(id) && hasTranslation;
    });

    if (translation) {
      return [...previous, { category, postCount: postCount.length }];
    }

    return previous;
  }, [] as CategoryCount[]);

  // TODO see if we can offload this calculation to the server - cound it on change and set a value of "enabled" or something
  const categories = categoryCount.reduce((categories, { category, postCount }) => {
    const langCategory = category.translations?.find(t => t.languages_code?.short_code === shortCode);

    if (postCount >= 5 && langCategory) {
      return [...categories, langCategory];
    }

    return categories;
  }, [] as Lucid.Directus.Generated.Tech_Blog_Category_Translations[]);

  return (
    <>
      <NavSkipButton />
      <BlogNav isTechBlog techBlogCategories={categories} />
    </>
  );
};

export default TechBlogNav;
